import Model, { attr } from '@ember-data/model';

import type { DateRangeInterface, DSSetup, ImageCropData } from 'webapp-types';
import type {
	AiSummaryContextLevel,
	AiSummaryLength,
	BlockDiscriminator,
	BlockVisualization,
	OrderDirection,
	SortFieldType,
	TableColumnType,
} from 'webapp/enums/enums';
import { BlockFontSize, BlockType, ChartLegendPosition } from 'webapp/enums/enums';
import { uniqueId } from 'webapp/utils/common';

import type { AiSummaryContentStatus } from 'webapp/components/block/content/ai-summary';

import type { DSMap } from 'webapp/models-custom/block';
import { Defaults } from 'webapp/models-custom/visualization/ai-summary';

export interface ColorSetting {
	dataSelectId: number;
	color: number | string; // number = color index from theme, string = custom hex
	dimension?: string;
	unit?: string;
}

interface YAxisBase {
	reverse?: boolean;
}

export interface YAxisScaleOff extends YAxisBase {
	disabled: true;
}

export interface YAxisScaleAuto extends YAxisBase {
	min: null;
	max: null;
}

export interface YAxisScaleManual extends YAxisBase {
	min: number | null;
	max: number | null;
}

export type YAxisScaleNotOff = YAxisScaleAuto | YAxisScaleManual;

export type YAxisScale = YAxisScaleOff | YAxisScaleAuto | YAxisScaleManual;

export default class BlockRecord extends Model {
	@attr('string') name!: string;
	@attr('number', { defaultValue: BlockType.UNI }) type!: number; // do we still need this?
	@attr('number') order!: number;
	@attr('number') discriminator!: BlockDiscriminator;

	@attr('number') boardId!: number;
	@attr('string') color!: string;
	@attr('object', {
		defaultValue() {
			return {};
		},
	})
	dateRange!: DateRangeInterface.FormulaInterface;
	@attr('number') originateFrom!: number;

	// visualization-connected props that will be serialized to 'settings' field
	@attr('string', { defaultValue: '' }) text!: string; // textblock
	@attr('boolean') omitTimestamp!: boolean; // textblock
	@attr('string') imageUrl!: string; // image
	@attr('string') imageOriginalUrl!: string; // image
	@attr('object') imageCrop!: ImageCropData | null; // image
	@attr('string') externalUrl!: string; // image
	@attr('string') labelHeader!: string; // table
	@attr('string', { defaultValue: BlockFontSize.MEDIUM }) fontSize!: BlockFontSize; // all

	@attr('string') sortOrder!: OrderDirection | null;
	@attr('number') itemsInLine!: number | null;
	@attr('boolean') firstSliceConversion!: boolean;
	@attr('boolean') hideStepConversion!: boolean;
	@attr('boolean') hideOverallConversionRate!: boolean;
	@attr('boolean') onlyMainMetricDimensions!: boolean;
	@attr('boolean') showOther!: boolean;
	@attr('number') icon!: number | null; // legacy has this directly on block, uni has in settings
	@attr('boolean') isSpline!: boolean;
	@attr('boolean') showTotals!: boolean; // line/bar for stacked
	@attr('boolean', { defaultValue: false }) mediaEnabled!: boolean;
	@attr('array', {
		defaultValue() {
			return [];
		},
	})
	yAxisScale!: YAxisScale[]; // line, bar, combo. array bcos of combo doubleaxis
	@attr('array', {
		defaultValue() {
			return [];
		},
	})
	colors!: ColorSetting[];

	@attr('number') visualization!: BlockVisualization;
	@attr('string') sortField!: SortFieldType | null;
	@attr('number') recordLimit!: number | null;
	@attr('number', { defaultValue: ChartLegendPosition.BOTTOM })
	declare legendPosition: ChartLegendPosition;
	@attr('boolean') hasRowNumbers!: boolean;
	@attr('number') tableColumnType!: TableColumnType | null;
	@attr('boolean') tableColumnInvert!: boolean; // TableColumnType.GRANULATIONS only
	@attr('object') dsMap!: DSMap | null;
	@attr('boolean', { defaultValue: false }) showTrafficLights!: boolean | null;
	@attr('boolean', { defaultValue: false }) hideTimeLeft!: boolean | null;

	// AI_SUMMARY visualization settings that will be serialized to 'settings.aiSummary'
	@attr('boolean', { defaultValue: Defaults.aiSummaryShowTrend }) aiSummaryShowTrend!: boolean;
	@attr('string', { defaultValue: Defaults.aiSummaryLanguage }) aiSummaryLanguage!: string;
	@attr('number', { defaultValue: Defaults.aiSummaryContextLevelType })
	aiSummaryContextLevelType!: AiSummaryContextLevel | null;
	@attr('number', { defaultValue: Defaults.aiSummaryLengthType })
	aiSummaryLengthType!: AiSummaryLength | null;
	@attr('string', { defaultValue: Defaults.aiSummaryEditedBy }) aiSummaryEditedBy?: string | null;
	@attr('number', { defaultValue: Defaults.aiSummaryEditedOn }) aiSummaryEditedOn?: number | null;
	@attr('number', { defaultValue: Defaults.aiSummaryStatus })
	aiSummaryStatus!: AiSummaryContentStatus;

	// datawall block position that will be serialized to 'settings.position' field
	@attr('number') posX!: number;
	@attr('number') posY!: number;
	@attr('number') width!: number;
	@attr('number') height!: number;
	@attr('boolean') posMigrated!: boolean;

	// only on creating new from metric (mapped from `fromMetric: {metricSetup, dateRange}`)
	@attr('object') fromMetricSetup!: DSSetup.MetricSetupData;
	@attr('object') fromMetricDateRange!: DateRangeInterface.FormulaInterface | null;

	// helper - not submitted to BE
	@attr('boolean') isGenerating!: boolean; // can be used to show unsaved blocks if set to true
	@attr('uniqueid', { defaultValue: () => uniqueId() as unknown as object }) localId!: number;
}
